/**
 * Filter memory for the admin and marketing pages: which URL params are
 * remembered on the account and restored when the page is opened without them.
 *
 * Only filter, search, sort, tab and view keys are listed. Pages that keep no
 * list state in the URL are omitted: /users, /event-maps, /b2b-service-maps,
 * /marketing/email-accounts, and /marketing/blocks (no list route; Blocks is a
 * tab of /marketing/templates).
 *
 * Keys deliberately LEFT OUT:
 * - `page` everywhere: paging.
 * - `perPage` (/api-keys, /permission-sets, /audit-log): read from bookmarks
 *   only, never written by the UI; paging.
 * - `run`, `statement` (/commission-runs): the open run and statement panel, a
 *   record selection rather than list state. The page has no other URL keys,
 *   so it has no rule.
 * - `requestId`, `recordId` (/audit-log): a lookup of one request or record,
 *   not a standing filter (coordinator, 2026-10-16).
 */
import type { FilterMemoryRule } from './types';

export const FILTER_MEMORY_ADMIN_MARKETING: readonly FilterMemoryRule[] = [
  {
    path: '/permission-sets',
    keep: [
      'search', // name search box
      'isActive', // Active / Retired tri-state filter
      'isSystem', // System / Custom tri-state filter
    ],
  },
  {
    path: '/api-keys',
    keep: [
      'search', // name search box
      'revoked', // Revoked tri-state filter
    ],
  },
  {
    path: '/commissions',
    keep: [
      'tab', // Schemes (absent) / Assignments section toggle
      'isActive', // schemes: Active / Inactive filter
      'schemeId', // assignments: scheme filter
      'userId', // assignments: person filter
      'periodId', // assignments: period filter
    ],
  },
  {
    path: '/audit-log',
    keep: [
      'from', // window start (absent = last 7 days)
      'to', // window end
      'userId', // actor filter
      'actorEmail', // actor email filter
      'anonymous', // anonymous-requests tri-state filter
      'method', // HTTP method filter
      'moduleSlug', // module filter
      'actionSlug', // action filter
      'route', // route pattern filter (exact)
      'path', // request path filter
      'statusCode', // exact status filter (exclusive with statusClass)
      'statusClass', // 1xx..5xx filter
      'failed', // failed-only tri-state filter
      'errorCode', // error code filter
      'minDurationMs', // slowest-requests threshold
      'sort', // ordering column
      'order', // ordering direction
    ],
  },
  {
    path: '/notification-log',
    keep: [
      'eventKey', // event filter
      'userId', // person filter
      'decision', // outcome filter
      'from', // window start
      'to', // window end
    ],
  },
  {
    path: '/audit-log/stats',
    keep: [
      'groupBy', // breakdown dimension
      'from', // window start
      'to', // window end
      'limit', // number of buckets shown
    ],
  },
  {
    path: '/settings',
    keep: [
      'tab', // Documents (absent) / Email / Notifications / Advanced
    ],
  },
  {
    path: '/marketing/campaigns',
    keep: [
      'q', // search box
      'status', // status filter
      'kind', // broadcast / sequence / A-B filter
    ],
  },
  {
    path: '/marketing/lists',
    keep: [
      'tab', // Lists (absent) / Subscribers
    ],
  },
  {
    path: '/marketing/templates',
    keep: [
      'tab', // Templates (absent) / Blocks / Variables
    ],
  },
  {
    path: '/marketing/settings',
    keep: ['tab'],
  },
];
